import { stackOrder } from "./priceTagStacking";
import { isPinned } from "./ownRoomMarker";
import React, { useEffect, useMemo, useRef } from "react";

export interface PriceTagMarker {
    id: string;
    lat: number;
    lng: number;
    label: string;
    isSelected?: boolean;
    /** The price behind the label, for which of stacked pins lies on top (`stackOrder`). */
    price?: number | null;
}

interface PriceTagMapProps {
    markers: PriceTagMarker[];
    mapOptions?: google.maps.MapOptions;
    className?: string;
    style?: React.CSSProperties;
    onMarkerClick?: (marker: PriceTagMarker) => void;
    fitBoundsPadding?: number | google.maps.Padding;
    zoomOnSingleMarker?: number;
}

const DEFAULT_PADDING = { top: 48, right: 48, bottom: 48, left: 48 } as const;
const DEFAULT_ZOOM_ON_SINGLE = 15;

const ensureStyleElement = () => {
    if (document.getElementById("map-price-tag-style")) {
        return;
    }

    const style = document.createElement("style");
    style.id = "map-price-tag-style";
    style.textContent = `
    .gm-style-iw-c {
      padding: 0 !important;
      background: transparent !important;
      box-shadow: none !important;
      max-width: none !important;
      border: none !important;
    }
    .gm-style-iw-d {
      overflow: visible !important;
      max-width: none !important;
    }
    .gm-style-iw-tc {
      display: none !important;
    }
    .gm-ui-hover-effect {
      display: none !important;
    }
  `;
    document.head.appendChild(style);
};

const createPriceTagHtml = (label: string, isSelected?: boolean) => {
    const background = isSelected ? "#DC2626" : "#111111";
    const padding = isSelected ? "8px 16px" : "6px 12px";
    const fontSize = isSelected ? "16px" : "14px";
    const arrowHeight = isSelected ? 14 : 12;
    const arrowWidth = isSelected ? 24 : 20;
    const arrowOffset = isSelected ? -14 : -12;
    const boxShadow = isSelected ? "0 6px 18px rgba(0,0,0,0.28)" : "0 4px 12px rgba(0,0,0,0.2)";
    const border = isSelected ? "2px solid rgba(255,255,255,0.85)" : "none";

    return `
    <div
      style="
        position: relative;
                padding: ${padding};
                font-size: ${fontSize};
        font-weight: bold;
        background: ${background};
        color: white;
        border-radius: 8px;
        text-align: center;
        min-width: 65px;
        margin-bottom: 8px;
                box-shadow: ${boxShadow};
                border: ${border};
      "
    >
      ${label}
      <div style="
        position: absolute;
        bottom: -12px;
                bottom: ${arrowOffset}px;
        transform: translateX(-50%);
                transform: translateX(-50%);
                width: ${arrowWidth}px;
                height: ${arrowHeight}px;
        background-color: ${background};
        clip-path: polygon(50% 100%, 0 0, 100% 0);
      "></div>
    </div>
  `;
};

const normalizePadding = (padding?: number | google.maps.Padding): google.maps.Padding => {
    if (typeof padding === "number") {
        return { top: padding, right: padding, bottom: padding, left: padding };
    }
    return padding ?? DEFAULT_PADDING;
};

const PriceTagMap: React.FC<PriceTagMapProps> = ({
    markers,
    mapOptions,
    className,
    style,
    onMarkerClick,
    fitBoundsPadding,
    zoomOnSingleMarker = DEFAULT_ZOOM_ON_SINGLE,
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const markerInstancesRef = useRef<google.maps.Marker[]>([]);
    const infoWindowsRef = useRef<google.maps.InfoWindow[]>([]);

    const combinedMapOptions = useMemo<google.maps.MapOptions>(() => {
        return {
            disableDefaultUI: true,
            clickableIcons: false,
            gestureHandling: "cooperative",
            ...mapOptions,
        };
    }, [mapOptions]);

    useEffect(() => {
        if (typeof window === "undefined" || !(window as any).google) {
            return;
        }
        ensureStyleElement();
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || !(window as any).google) {
            return;
        }

        if (!containerRef.current) {
            return;
        }

        const google = (window as any).google as typeof globalThis.google;

        if (!mapRef.current) {
            mapRef.current = new google.maps.Map(containerRef.current, combinedMapOptions);
        } else {
            mapRef.current.setOptions(combinedMapOptions);
        }

        const map = mapRef.current;

        // Clean previous markers
        infoWindowsRef.current.forEach((infoWindow) => infoWindow.close());
        markerInstancesRef.current.forEach((marker) => marker.setMap(null));
        infoWindowsRef.current = [];
        markerInstancesRef.current = [];

        const validMarkers = markers.filter(isPinned);

        if (validMarkers.length === 0) {
            return;
        }

        const bounds = new google.maps.LatLngBounds();
        const orderedMarkers = stackOrder(validMarkers);
        const selectedZIndexBase = (google.maps.Marker.MAX_ZINDEX || 1000) + 1;

        // An explicit z-index per pin: left unset, Google stacks pins at one
        // coordinate by its own rule, not by the order they are drawn in.
        orderedMarkers.forEach((marker, index) => {
            const zIndex = marker.isSelected ? selectedZIndexBase : index + 1;
            const position: google.maps.LatLngLiteral = {
                lat: marker.lat,
                lng: marker.lng,
            };

            const googleMarker = new google.maps.Marker({
                position,
                map,
                icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 0,
                },
                zIndex,
            });

            const infoWindow = new google.maps.InfoWindow({
                content: createPriceTagHtml(marker.label, marker.isSelected),
                pixelOffset: new google.maps.Size(0, -6),
                disableAutoPan: true,
                zIndex,
            });

            infoWindow.open(map, googleMarker);

            if (marker.isSelected) {
                googleMarker.setZIndex(selectedZIndexBase);
                infoWindow.setZIndex(selectedZIndexBase);
            }

            if (onMarkerClick) {
                googleMarker.addListener("click", () => onMarkerClick(marker));
            }

            markerInstancesRef.current.push(googleMarker);
            infoWindowsRef.current.push(infoWindow);
            bounds.extend(position);
        });

        if (validMarkers.length === 1) {
            map.setCenter(bounds.getCenter());
            if (zoomOnSingleMarker) {
                map.setZoom(zoomOnSingleMarker);
            }
        } else {
            map.fitBounds(bounds, normalizePadding(fitBoundsPadding));
        }

        return () => {
            infoWindowsRef.current.forEach((infoWindow) => infoWindow.close());
            markerInstancesRef.current.forEach((marker) => marker.setMap(null));
            infoWindowsRef.current = [];
            markerInstancesRef.current = [];
        };
    }, [markers, combinedMapOptions, fitBoundsPadding, onMarkerClick, zoomOnSingleMarker]);

    return <div ref={containerRef} className={className} style={style} />;
};

export default PriceTagMap;
